/**
 * Admin Jobs Section
 *
 * Displays jobs grouped by company in an accordion structure.
 * Each company shows Active and Past jobs as expandable child lists.
 */

import { useState, useMemo } from 'react';
import {
  useAdminActiveJobs,
  useAdminRecentJobs,
  useAdminScheduledJobs,
  useAdminJobStats,
  useCancelJob,
  useAdminBuildProgress,
} from '../../hooks/useAdminCosts';
import type { AdminJobItem, ScheduledJobItem, JobStatus } from '../../types';
import type { BuildProgressItem } from '../../services/api';

// Status badge colors
const STATUS_COLORS: Record<JobStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  queued: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
  rate_limited: 'bg-orange-100 text-orange-800',
};

// Priority badge colors
const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  NORMAL: 'bg-blue-100 text-blue-800',
  LOW: 'bg-gray-100 text-gray-600',
};

// Job type display names
const JOB_TYPE_NAMES: Record<string, string> = {
  full_build: 'Full Build',
  incremental_update: 'Incremental Update',
  news_refresh: 'News Refresh',
  stock_refresh: 'Stock Refresh',
  leadership_refresh: 'Leadership Refresh',
  jobs_refresh: 'Jobs Refresh',
};

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function formatFutureTime(dateString: string | null): string {
  if (!dateString) return 'Not scheduled';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs < 0) return 'Overdue';

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `in ${diffMins}m`;
  if (diffHours < 24) return `in ${diffHours}h`;
  return `in ${diffDays}d`;
}

interface JobStatCardProps {
  label: string;
  value: number;
  color: string;
}

function JobStatCard({ label, value, color }: JobStatCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

// Group jobs by company
interface CompanyJobGroup {
  companyId: string;
  companyName: string;
  activeJobs: AdminJobItem[];
  pastJobs: AdminJobItem[];
}

function groupJobsByCompany(jobs: AdminJobItem[]): CompanyJobGroup[] {
  const groups = new Map<string, CompanyJobGroup>();

  for (const job of jobs) {
    const key = job.company_id || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        companyId: job.company_id || 'unknown',
        companyName: job.company_name || 'Unknown Company',
        activeJobs: [],
        pastJobs: [],
      });
    }

    const group = groups.get(key)!;
    const isActive = ['running', 'queued', 'pending', 'rate_limited'].includes(job.status);
    if (isActive) {
      group.activeJobs.push(job);
    } else {
      group.pastJobs.push(job);
    }
  }

  // Sort groups by company name
  return Array.from(groups.values()).sort((a, b) =>
    a.companyName.localeCompare(b.companyName)
  );
}

// Chevron icons
function ChevronDown({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

interface JobRowProps {
  job: AdminJobItem;
  onCancel: (jobId: string) => void;
  isCancelling: boolean;
  indent?: boolean;
}

function JobRow({ job, onCancel, isCancelling, indent = false }: JobRowProps) {
  return (
    <tr className={`hover:bg-gray-50 ${indent ? 'bg-gray-25' : ''}`}>
      <td className={`px-4 py-2 text-sm text-gray-600 ${indent ? 'pl-12' : ''}`}>
        {JOB_TYPE_NAMES[job.job_type] || job.job_type}
      </td>
      <td className="px-4 py-2">
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            STATUS_COLORS[job.status]
          }`}
        >
          {job.status}
        </span>
      </td>
      <td className="px-4 py-2">
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            PRIORITY_COLORS[job.priority]
          }`}
        >
          {job.priority}
        </span>
      </td>
      <td className="px-4 py-2 text-sm text-gray-500">
        {formatRelativeTime(job.started_at || job.created_at)}
      </td>
      <td className="px-4 py-2 text-sm text-gray-500">{job.triggered_by || '-'}</td>
      <td className="px-4 py-2">
        {['running', 'queued', 'pending', 'rate_limited'].includes(job.status) && (
          <button
            onClick={() => onCancel(job.id)}
            disabled={isCancelling}
            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </td>
    </tr>
  );
}

interface CompanyAccordionProps {
  group: CompanyJobGroup;
  onCancel: (jobId: string) => void;
  isCancelling: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function CompanyAccordion({
  group,
  onCancel,
  isCancelling,
  isExpanded,
  onToggle,
}: CompanyAccordionProps) {
  const [showActive, setShowActive] = useState(true);
  const [showPast, setShowPast] = useState(false);

  const activeCount = group.activeJobs.length;
  const pastCount = group.pastJobs.length;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Company Header Row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          <span className="font-medium text-gray-900">{group.companyName}</span>
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
              {activeCount} active
            </span>
          )}
          {pastCount > 0 && (
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
              {pastCount} past
            </span>
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-100">
          {/* Active Jobs Section */}
          {activeCount > 0 && (
            <div className="border-b border-gray-100">
              <button
                onClick={() => setShowActive(!showActive)}
                className="w-full flex items-center gap-2 px-6 py-2 text-sm font-medium text-blue-700 hover:bg-gray-100"
              >
                {showActive ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                Active Jobs ({activeCount})
              </button>
              {showActive && (
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase pl-12">
                        Type
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Priority
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Started
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Triggered By
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {group.activeJobs.map((job) => (
                      <JobRow
                        key={job.id}
                        job={job}
                        onCancel={onCancel}
                        isCancelling={isCancelling}
                        indent
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Past Jobs Section */}
          {pastCount > 0 && (
            <div>
              <button
                onClick={() => setShowPast(!showPast)}
                className="w-full flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                {showPast ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                Past Jobs ({pastCount})
              </button>
              {showPast && (
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase pl-12">
                        Type
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Priority
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Completed
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Triggered By
                      </th>
                      <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {group.pastJobs.slice(0, 10).map((job) => (
                      <JobRow
                        key={job.id}
                        job={job}
                        onCancel={onCancel}
                        isCancelling={isCancelling}
                        indent
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* No jobs message */}
          {activeCount === 0 && pastCount === 0 && (
            <div className="px-6 py-3 text-sm text-gray-500">No jobs for this company</div>
          )}
        </div>
      )}
    </div>
  );
}

interface CompanyJobsListProps {
  jobs: AdminJobItem[];
  isLoading: boolean;
  error: Error | null;
  onCancel: (jobId: string) => void;
  isCancelling: boolean;
}

function CompanyJobsList({
  jobs,
  isLoading,
  error,
  onCancel,
  isCancelling,
}: CompanyJobsListProps) {
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());

  const companyGroups = useMemo(() => groupJobsByCompany(jobs), [jobs]);

  // Auto-expand companies with active jobs
  useMemo(() => {
    const withActive = companyGroups
      .filter((g) => g.activeJobs.length > 0)
      .map((g) => g.companyId);
    if (withActive.length > 0 && expandedCompanies.size === 0) {
      setExpandedCompanies(new Set(withActive));
    }
  }, [companyGroups]);

  const toggleCompany = (companyId: string) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-sm">Error loading jobs: {error.message}</div>;
  }

  if (companyGroups.length === 0) {
    return <div className="text-gray-500 text-sm py-4 text-center">No jobs found</div>;
  }

  return (
    <div className="divide-y divide-gray-200">
      {companyGroups.map((group) => (
        <CompanyAccordion
          key={group.companyId}
          group={group}
          onCancel={onCancel}
          isCancelling={isCancelling}
          isExpanded={expandedCompanies.has(group.companyId)}
          onToggle={() => toggleCompany(group.companyId)}
        />
      ))}
    </div>
  );
}

interface ScheduledJobsTableProps {
  jobs: ScheduledJobItem[];
  isLoading: boolean;
  error: Error | null;
}

function ScheduledJobsTable({ jobs, isLoading, error }: ScheduledJobsTableProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">Error loading scheduled jobs: {error.message}</div>
    );
  }

  if (jobs.length === 0) {
    return <div className="text-gray-500 text-sm py-4 text-center">No scheduled jobs</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Schedule
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Trigger Type
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Next Run
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {jobs.map((job) => (
            <tr key={job.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{job.name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{job.trigger_description}</td>
              <td className="px-4 py-3">
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                  {job.trigger_type}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {formatFutureTime(job.next_run_time)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Phase status colors
const PHASE_STATUS_COLORS: Record<string, string> = {
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-gray-100 text-gray-500',
};

interface BuildProgressTableProps {
  builds: BuildProgressItem[];
  isLoading: boolean;
  error: Error | null;
}

function BuildProgressTable({ builds, isLoading, error }: BuildProgressTableProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-sm">Error loading build progress: {error.message}</div>;
  }

  if (builds.length === 0) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center">
        <div className="text-4xl mb-2">No active KB builds</div>
        <p>KB builds will appear here when running</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {builds.map((build) => (
        <div key={build.company_id} className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{build.company_id}</h3>
              <p className="text-xs text-gray-500">
                Updated: {new Date(build.updated_at).toLocaleTimeString()}
              </p>
            </div>
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                PHASE_STATUS_COLORS[build.status] || 'bg-gray-100'
              }`}
            >
              {build.status}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{build.current_phase_label}</span>
              <span>{Math.round(build.progress_pct)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${build.progress_pct}%` }}
              />
            </div>
          </div>

          {/* Phase info */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              Phase {build.phase_index + 1} of {build.total_phases}
            </span>
            {build.message && <span className="text-gray-600">{build.message}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminJobsSection() {
  const [activeTab, setActiveTab] = useState<'jobs' | 'scheduled' | 'progress'>('jobs');

  // Fetch both active and recent jobs to combine them
  const activeJobsQuery = useAdminActiveJobs(100);
  const recentJobsQuery = useAdminRecentJobs(100);
  const scheduledJobsQuery = useAdminScheduledJobs();
  const jobStatsQuery = useAdminJobStats();
  const cancelJobMutation = useCancelJob();
  const buildProgressQuery = useAdminBuildProgress();

  // Combine and deduplicate jobs
  const allJobs = useMemo(() => {
    const jobMap = new Map<string, AdminJobItem>();
    // Add active jobs first (they have priority)
    for (const job of activeJobsQuery.data ?? []) {
      jobMap.set(job.id, job);
    }
    // Add recent jobs if not already present
    for (const job of recentJobsQuery.data ?? []) {
      if (!jobMap.has(job.id)) {
        jobMap.set(job.id, job);
      }
    }
    return Array.from(jobMap.values());
  }, [activeJobsQuery.data, recentJobsQuery.data]);

  const handleCancelJob = (jobId: string) => {
    if (confirm('Are you sure you want to cancel this job?')) {
      cancelJobMutation.mutate(jobId);
    }
  };

  // Count companies with active jobs
  const companiesWithActiveJobs = useMemo(() => {
    const groups = groupJobsByCompany(allJobs);
    return groups.filter((g) => g.activeJobs.length > 0).length;
  }, [allJobs]);

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Jobs</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <JobStatCard
          label="Running"
          value={jobStatsQuery.data?.running_count ?? 0}
          color="text-blue-600"
        />
        <JobStatCard
          label="Queued"
          value={jobStatsQuery.data?.queued_count ?? 0}
          color="text-yellow-600"
        />
        <JobStatCard
          label="Pending"
          value={jobStatsQuery.data?.pending_count ?? 0}
          color="text-gray-600"
        />
        <JobStatCard
          label="Completed Today"
          value={jobStatsQuery.data?.completed_today ?? 0}
          color="text-green-600"
        />
        <JobStatCard
          label="Failed Today"
          value={jobStatsQuery.data?.failed_today ?? 0}
          color="text-red-600"
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'jobs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Jobs by Company
            {companiesWithActiveJobs > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs">
                {companiesWithActiveJobs} active
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'scheduled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Scheduled Jobs
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === 'progress'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Build Progress
            {buildProgressQuery.data && buildProgressQuery.data.total_active > 0 && (
              <span className="ml-2 bg-green-100 text-green-600 py-0.5 px-2 rounded-full text-xs">
                {buildProgressQuery.data.total_active} active
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {activeTab === 'jobs' && (
          <CompanyJobsList
            jobs={allJobs}
            isLoading={activeJobsQuery.isLoading || recentJobsQuery.isLoading}
            error={activeJobsQuery.error || recentJobsQuery.error}
            onCancel={handleCancelJob}
            isCancelling={cancelJobMutation.isPending}
          />
        )}
        {activeTab === 'scheduled' && (
          <div className="p-4">
            <ScheduledJobsTable
              jobs={scheduledJobsQuery.data ?? []}
              isLoading={scheduledJobsQuery.isLoading}
              error={scheduledJobsQuery.error}
            />
          </div>
        )}
        {activeTab === 'progress' && (
          <div className="p-4">
            <BuildProgressTable
              builds={buildProgressQuery.data?.active_builds ?? []}
              isLoading={buildProgressQuery.isLoading}
              error={buildProgressQuery.error}
            />
          </div>
        )}
      </div>
    </section>
  );
}

export default AdminJobsSection;
